/**
 * HaulerCentral Token Interceptor
 *
 * Handles automatic token refresh on 401 responses.
 * Queues requests during refresh and retries them after successful refresh.
 * Following patterns from @commander/auth.
 */

import { AUTH_ENDPOINTS, AUTH_EVENTS } from '../constants/authConstants';
import * as tokenProvider from '../lib/tokenProvider';
import type { AuthTokens } from '../types/auth.types';

// API Base URL
const getApiBaseUrl = (): string => {
  const configured = import.meta.env?.VITE_API_BASE_URL;
  if (typeof configured !== 'string' || configured.trim().length === 0) {
    throw new Error('[HaulerCentral Auth] Missing required VITE_API_BASE_URL');
  }
  return configured.replace(/\/+$/, '');
};

// Request queue for concurrent requests during token refresh
interface QueuedRequest {
  resolve: (value: any) => void;
  reject: (reason: any) => void;
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
  headers?: Record<string, string>;
  retryCount?: number;
}

let refreshQueue: QueuedRequest[] = [];
let isRefreshing = false;
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 1;
const MAX_REQUEST_RETRY = 1;

/**
 * Emit auth refresh events for UI updates
 */
function emitRefreshEvent(type: 'start' | 'complete' | 'error'): void {
  const eventKey = `TOKEN_REFRESH_${type.toUpperCase()}` as const;
  const eventName = AUTH_EVENTS[eventKey as keyof typeof AUTH_EVENTS];
  window.dispatchEvent(new CustomEvent(eventName));
}

/**
 * Process queued requests after successful token refresh
 */
function processQueue(error: any): void {
  refreshQueue.forEach(({ resolve, reject, endpoint, method, body, headers, retryCount = 0 }) => {
    if (error) {
      reject(error);
    } else if (retryCount < MAX_REQUEST_RETRY) {
      // Retry original request with new token
      authRequest(endpoint, method, body, headers, retryCount + 1)
        .then(resolve)
        .catch(reject);
    } else {
      reject(new Error('Session expired. Please log in again.'));
    }
  });

  // Clear queue
  refreshQueue = [];
}

/**
 * Attempt to refresh access token
 */
export async function attemptTokenRefresh(): Promise<boolean> {
  // Prevent infinite refresh loops
  if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
    processQueue(new Error('Session expired. Please log in again.'));
    return false;
  }

  refreshAttempts++;
  isRefreshing = true;
  emitRefreshEvent('start');

  try {
    const refreshToken = tokenProvider.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const API_BASE_URL = getApiBaseUrl();
    const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.REFRESH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();

    if (data.success && data.data?.accessToken) {
      // Update tokens in storage via tokenProvider
      const tokens: AuthTokens = {
        accessToken: data.data.accessToken,
        refreshToken: data.data.refreshToken || refreshToken,
      };
      tokenProvider.setTokens(tokens);

      // Reset refresh attempts on success
      refreshAttempts = 0;
      emitRefreshEvent('complete');
      processQueue(null);
      return true;
    }

    return false;
  } catch (error) {
    // Refresh failed - clear tokens and redirect to login
    tokenProvider.clearAuth();
    emitRefreshEvent('error');
    processQueue(error);

    // Redirect to login - let the app handle the redirect
    window.dispatchEvent(new CustomEvent('auth:logout'));

    return false;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Make authenticated API request with automatic token refresh
 */
export async function authRequest<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: any,
  customHeaders?: Record<string, string>,
  retryCount: number = 0
): Promise<T> {
  // Prevent infinite retry loops
  if (retryCount > MAX_REQUEST_RETRY) {
    tokenProvider.clearAuth();
    window.dispatchEvent(new CustomEvent('auth:logout'));
    throw new Error('Session expired. Please log in again.');
  }

  const API_BASE_URL = getApiBaseUrl();
  const url = `${API_BASE_URL}${endpoint}`;

  // Get auth header
  const authHeader = tokenProvider.getAuthHeader();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(authHeader || {}),
    ...customHeaders,
  };

  const config: RequestInit = {
    method,
    headers,
    credentials: 'include',
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  let response: Response;

  try {
    response = await fetch(url, config);
  } catch (_error) {
    throw new Error('Network error. Please check your connection.');
  }

  // Handle 401 Unauthorized - try to refresh token
  if (response.status === 401) {
    // If already refreshing, add to queue
    if (isRefreshing) {
      return new Promise<T>((resolve, reject) => {
        refreshQueue.push({
          resolve,
          reject,
          endpoint,
          method,
          body,
          headers: customHeaders,
          retryCount,
        });
      });
    }

    // Not currently refreshing, attempt refresh
    const refreshed = await attemptTokenRefresh();

    if (refreshed) {
      // Retry original request with new token (increment retry count)
      return authRequest<T>(endpoint, method, body, customHeaders, retryCount + 1);
    }

    // Refresh failed, throw error
    throw new Error('Session expired. Please log in again.');
  }

  // Handle other errors
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Make public API request (no auth required)
 */
export async function publicRequest<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body?: any
): Promise<T> {
  const API_BASE_URL = getApiBaseUrl();
  const url = `${API_BASE_URL}${endpoint}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const config: RequestInit = {
    method,
    headers,
    credentials: 'include',
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(url, config);

  // Handle 401 Unauthorized - clear tokens and redirect to login
  if (response.status === 401) {
    tokenProvider.clearAuth();
    window.dispatchEvent(new CustomEvent('auth:logout'));
    throw new Error('Unauthorized. Please log in again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Check if token is currently being refreshed
 */
export function isTokenRefreshing(): boolean {
  return isRefreshing;
}

export default {
  authRequest,
  publicRequest,
  attemptTokenRefresh,
  isTokenRefreshing,
};
